// Kill-Switch (world.json bikerBars "killswitch"): the Kaminari-gumi junkyard bar, a 6x5-tile yard in the Shin-Okubo mart
// block, open to the alley on its west side (x143-144) and to the south sidewalk. Car shells and tyre stacks wall it in
// (flat decals along the north and east edges), the bar counter is the gutted front of a bus, and oil-drum fires light it.
// Presentation only: no collision, and every prop stays east of the west column (x145) so the alley entrance is clear.
// Palette comes from hd-kit (theme.ts); the sign uses the glyph table's katakana. Called from hd-scene.ts
// (paintKillSwitch on the ground before the baked lights, killSwitchProps with the other props).
import { WORLD, type Rect } from '../../common/src/data';
import { TILE, inTileRect } from '../../common/src/map';
import { hash2 } from '../../common/src/util';
import { canvas } from './canvas';
import { type G, K, HT, px, frame, disc, dk, lt, alpha, dither, speckle, shadow, text } from './hd-kit';
import type { LightFn } from './hd-registry';
import type { HdLight } from './hd-scene';
import { barrel, type Sprite } from './hd-place-exhaust';

type Prop = (cv: HTMLCanvasElement, wx: number, wy: number, ax: number, ay: number) => void;
const bar = () => WORLD.bikerBars?.find(b => b.id === 'killswitch');

function glowBlob(g: G, x: number, y: number, rx: number, ry: number, col: string, a: number) {
  g.save(); g.globalCompositeOperation = 'lighter'; g.translate(x, y); g.scale(1, ry / rx);
  const grd = g.createRadialGradient(0, 0, 0, 0, 0, rx); grd.addColorStop(0, alpha(col, a)); grd.addColorStop(1, alpha(col, 0));
  g.fillStyle = grd; g.fillRect(-rx, -rx, rx * 2, rx * 2); g.restore();
}
/** a stripped car shell seen from the side: no glass, no wheels, rust bleeding from the sills */
function shell(g: G, x: number, y: number, w: number, body: string, seed: number) {
  const h = 16;
  px(g, x, y + 5, w, h - 5, K.ink); px(g, x + 1, y + 6, w - 2, h - 8, body); // lower body
  px(g, x + 1, y + 6, w - 2, 2, lt(body, 0.3));
  const rx = x + Math.round(w * 0.22), rw = Math.round(w * 0.5);
  px(g, rx, y, rw, 7, K.ink); px(g, rx + 1, y + 1, rw - 2, 5, body); // cabin
  px(g, rx + 3, y + 2, rw / 2 - 4, 4, K.ink); px(g, rx + rw / 2 + 2, y + 2, rw / 2 - 5, 4, K.ink); // empty window frames
  px(g, x + 2, y + h - 3, w - 4, 2, K.woodLo); // rusted sill
  for (const wx of [x + 7, x + w - 13]) px(g, wx, y + h - 3, 7, 3, K.ink); // empty wheel arches
  speckle(g, x + 1, y + 6, w - 2, h - 8, K.woodHi, 0.12, seed);
  px(g, x + w - 4, y + 8, 2, 3, K.warmHi); // one surviving lamp
}
/** a pile of tyres seen from above and front */
function tyre(g: G, cx: number, cy: number) {
  disc(g, cx, cy, 9, 6, K.ink); disc(g, cx, cy, 8, 5, K.tarHi); disc(g, cx, cy - 1, 7, 4, K.tar);
  disc(g, cx, cy, 4, 2, K.ink); disc(g, cx, cy, 2, 1, K.metalLo); px(g, cx - 6, cy - 4, 5, 1, K.tarHi);
}

// ================================================================== ground (hd px, scene-relative)
export function paintKillSwitch(g: G, R: Rect, L: LightFn) {
  const b = bar(); if (!b || !inTileRect(b.x, b.y, R)) return;
  const ox = (b.x - R.x) * HT, oy = (b.y - R.y) * HT, W = b.w * HT, H = b.h * HT;
  g.save(); g.beginPath(); g.rect(ox, oy, W, H); g.clip();

  // ---- oil-soaked concrete: slabs, cracks, dark pools with a pink-cyan sheen, tyre tracks from the alley
  px(g, ox, oy, W, H, K.concrete);
  for (let j = 0; j < H; j += 40) px(g, ox, oy + j, W, 1, K.concreteLo);
  for (let i = 0; i < W; i += 48) px(g, ox + i + (i % 96 ? 0 : 8), oy, 1, H, K.concreteLo);
  speckle(g, ox, oy, W, H, alpha(K.night, 0.35), 0.07, 91, 2);
  for (let i = 0; i < 12; i++) {
    const cx = ox + 36 + Math.floor(hash2(i, 3) * (W - 60)), cy = oy + 50 + Math.floor(hash2(5, i) * (H - 60));
    const rx = 8 + Math.floor(hash2(i, 8) * 16), ry = 3 + Math.floor(rx * 0.4);
    disc(g, cx, cy, rx, ry, alpha(K.night, 0.45));
    dither(g, cx - rx + 3, cy - ry + 1, Math.max(2, rx - 4), 2, alpha(i % 2 ? K.pink : K.cyan, 0.2));
  }
  for (const [sy, a] of [[96, 0], [108, 1], [128, 2]] as const) for (let t = 0; t < 110; t++) {
    if (t % 5 !== 4) px(g, ox + t, oy + sy + Math.round(Math.sin((t + a * 30) / 20) * 3), 1, 3, alpha(K.night, 0.3));
  }

  // ---- north wall of the yard (v0-44): the mart's back plate with two stacks of car shells on top of each other
  px(g, ox, oy, W, 44, K.ink); px(g, ox, oy + 2, W, 40, K.wallLo);
  for (let i = 0; i < W; i += 12) px(g, ox + i, oy + 2, 1, 40, K.ink);
  const cols = [K.metal, dk(K.red, 0.25), K.metalHi, dk(K.cyan, 0.55), K.woodHi, K.metal];
  for (let k = 0; k < 4; k++) { // each stack: two shells, the top one set off by a few px
    const sx = ox + 6 + k * 46;
    shell(g, sx + (k % 2) * 4, oy + 21, 42, cols[(k * 2) % 6], k * 2 + 1);
    shell(g, sx + 3 - (k % 2) * 4, oy + 5, 38, cols[(k * 2 + 1) % 6], k * 2 + 2);
  }
  px(g, ox, oy + 42, W, 3, alpha(K.night, 0.55)); px(g, ox, oy + 45, W, 4, alpha(K.night, 0.25));

  // ---- east wall: columns of tyre stacks along the edge
  for (let j = 52; j < H - 4; j += 12) for (const dx of [W - 14, W - 28]) if (dx === W - 14 || j % 24 === 4) tyre(g, ox + dx, oy + j + 5);
  px(g, ox + W - 3, oy + 44, 3, H - 44, alpha(K.night, 0.5));

  // ---- flickering sign over the bus: a scrap-steel plate, red kill-switch on the left, キルスイッチ in warm neon
  const sx = ox + 36, sy = oy + 8, sw = 112, sh = 28;
  px(g, sx, sy, sw, sh, K.ink); px(g, sx + 1, sy + 1, sw - 2, sh - 2, dk(K.red, 0.7));
  frame(g, sx + 2, sy + 2, sw - 4, sh - 4, dk(K.red, 0.3));
  for (const bx of [sx + 4, sx + sw - 7]) for (const by of [sy + 4, sy + sh - 7]) px(g, bx, by, 3, 3, K.metalHi); // bolts
  // the switch: grey housing, a slot, and the big red lever thrown to OFF
  const hx = sx + 9, hy = sy + 5;
  px(g, hx, hy, 17, 18, K.ink); px(g, hx + 1, hy + 1, 15, 16, K.metalLo); px(g, hx + 1, hy + 1, 15, 2, K.metal);
  px(g, hx + 6, hy + 4, 5, 10, K.ink);
  for (let t = 0; t < 8; t++) px(g, hx + 8 - Math.round(t * 0.35), hy + 12 - t, 3, 2, K.red); // lever
  disc(g, hx + 6, hy + 4, 3, 3, K.ink); disc(g, hx + 6, hy + 4, 2, 2, K.red); px(g, hx + 5, hy + 3, 1, 1, lt(K.red, 0.5));
  const tx = sx + 32, ty = sy + 8;
  for (const [dx, dy] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) text(g, 'キルスイッチ', tx + dx, ty + dy, K.ink, 2, 1);
  text(g, 'キルスイッチ', tx, ty, lt(K.warm, 0.35), 2, 1);
  px(g, sx + 14, sy - 6, 2, 6, K.ink); px(g, sx + sw - 16, sy - 6, 2, 6, K.ink); // chains to the wall
  L(sx, sy, sw, sh, K.red, 0.45, true, true);
  L(sx - 24, sy + sh, sw + 48, 54, K.red, 0.08);
  glowBlob(g, sx + sw / 2, sy + sh / 2, 52, 18, K.red, 0.1);

  // string of bare bulbs under the stacks
  for (let t = 0; t < W; t += 2) px(g, ox + t, oy + 47 + Math.round(3 * Math.sin(Math.PI * (t % 48) / 48)), 2, 1, K.ink);
  for (let t = 12; t < W; t += 24) {
    const y = oy + 49 + Math.round(3 * Math.sin(Math.PI * (t % 48) / 48));
    px(g, ox + t - 1, y, 3, 4, K.ink); px(g, ox + t, y + 1, 1, 2, K.warmHi); glowBlob(g, ox + t, y + 2, 7, 6, K.warm, 0.3);
    L(ox + t - 10, y - 4, 20, 14, K.amber, 0.12);
  }
  g.restore();
}

// ================================================================== props (y-sorted, no collision)
/** the front of a gutted bus: windscreen stripped to a shelf of bottles, a grille, and its bumper as the bar-top plank */
function bus(): Sprite {
  const [cv, g] = canvas(96, 64), ax = 48, ay = 60;
  shadow(g, 4, 54, 92, 6, 0.4);
  const body = dk(K.warm, 0.35);
  px(g, 4, 6, 88, 48, K.ink); px(g, 5, 7, 86, 46, body); px(g, 5, 7, 86, 3, lt(body, 0.3)); px(g, 5, 49, 86, 4, dk(body, 0.4));
  speckle(g, 5, 7, 86, 46, K.woodLo, 0.12, 14); speckle(g, 5, 7, 86, 46, K.woodHi, 0.05, 15);
  px(g, 12, 10, 72, 7, K.ink); px(g, 14, 11, 68, 5, K.night); // destination board, dead
  px(g, 12, 20, 34, 18, K.ink); px(g, 50, 20, 34, 18, K.ink); // windscreen panes, glass gone
  for (const x of [14, 52]) { px(g, x, 22, 30, 14, K.wallLo); for (let i = 0; i < 5; i++) { const c = [K.glassHi, K.warm, K.pink, K.leafHi, K.white][i]; px(g, x + 2 + i * 6, 25, 3, 7, K.ink); px(g, x + 3 + i * 6, 26, 1, 5, c); } px(g, x, 33, 30, 3, K.woodHi); }
  px(g, 46, 20, 4, 18, K.ink); px(g, 47, 20, 2, 18, K.metalLo); // the centre post
  for (const x of [8, 78]) { px(g, x, 40, 10, 8, K.ink); px(g, x + 1, 41, 8, 6, K.warmHi); px(g, x + 1, 41, 8, 2, K.white); } // headlights
  px(g, 28, 40, 40, 9, K.ink); for (let i = 0; i < 40; i += 3) px(g, 28 + i, 41, 2, 7, i % 6 ? K.metalLo : K.metal);
  px(g, 38, 10, 20, 2, K.red); // old route stripe
  px(g, 0, 50, 96, 10, K.ink); px(g, 1, 51, 94, 8, K.metalHi); px(g, 1, 51, 94, 2, lt(K.metalHi, 0.3)); px(g, 1, 57, 94, 2, K.metalLo); // bumper bar-top
  for (const x of [8, 24, 50, 70]) { px(g, x, 44, 4, 6, K.ink); px(g, x + 1, 45, 2, 4, x % 3 ? K.warm : K.pink); } // bottles on the plank
  for (const x of [38, 80]) { px(g, x, 46, 5, 4, K.ink); px(g, x + 1, 47, 3, 2, K.white); } // cups
  return { cv, ax, ay };
}
/** a stool of two stacked tyres */
function tyreStool(): Sprite {
  const [cv, g] = canvas(26, 26), ax = 13, ay = 23;
  shadow(g, 3, 18, 22, 4, 0.4);
  disc(g, 13, 16, 10, 5, K.ink); disc(g, 13, 15, 9, 4, K.tarHi);
  disc(g, 13, 10, 10, 5, K.ink); disc(g, 13, 9, 9, 4, K.tar); disc(g, 13, 9, 4, 2, K.ink); disc(g, 13, 9, 3, 1, K.red); px(g, 8, 6, 5, 1, K.tarHi);
  return { cv, ax, ay };
}
/** a wrecked car on blocks, its back seat a sofa: side view */
function carSofa(): Sprite {
  const [cv, g] = canvas(76, 40), ax = 38, ay = 37;
  shadow(g, 3, 31, 70, 5, 0.4);
  for (const x of [8, 56]) { px(g, x, 28, 12, 6, K.ink); px(g, x + 1, 29, 10, 4, K.concreteHi); }
  shell(g, 4, 12, 68, dk(K.cyan, 0.6), 21);
  px(g, 20, 11, 36, 8, K.ink); px(g, 22, 12, 32, 6, K.night);
  for (const x of [24, 40]) { px(g, x, 8, 13, 12, K.ink); px(g, x + 1, 9, 11, 10, K.red); px(g, x + 1, 9, 11, 2, lt(K.red, 0.3)); } // two bucket seats showing through
  return { cv, ax, ay };
}
/** a cable-drum table with a flickering oil lamp */
function drumTable(): Sprite {
  const [cv, g] = canvas(40, 40), ax = 20, ay = 36;
  shadow(g, 4, 31, 32, 5, 0.4);
  px(g, 10, 14, 20, 18, K.ink); px(g, 11, 15, 18, 16, K.wood); px(g, 11, 15, 3, 16, K.woodHi); px(g, 26, 15, 3, 16, K.woodLo);
  disc(g, 20, 14, 15, 6, K.ink); disc(g, 20, 14, 14, 5, K.woodHi); disc(g, 19, 13, 9, 3, lt(K.woodHi, 0.2));
  px(g, 10, 12, 5, 5, K.ink); px(g, 11, 13, 3, 3, K.white); px(g, 26, 13, 5, 5, K.ink); px(g, 27, 14, 3, 3, K.white);
  px(g, 19, 5, 3, 8, K.ink); px(g, 20, 6, 1, 6, K.glassHi); px(g, 20, 2, 1, 4, K.warmHi); glowBlob(g, 20, 6, 10, 8, K.warm, 0.25);
  return { cv, ax, ay };
}

export function killSwitchProps(R: Rect, prop: Prop, lights: HdLight[]) {
  const b = bar(); if (!b || !inTileRect(b.x, b.y, R)) return;
  const bx = b.x * TILE, by = b.y * TILE;
  const put = (s: Sprite, u: number, v: number) => prop(s.cv, bx + u / 2, by + v / 2, s.ax, s.ay);
  const pool = (u: number, v: number, w: number, h: number, color: string, a: number) =>
    lights.push({ x: bx + u / 2 - w / 2, y: by + v / 2 - h / 2, w, h, color, alpha: a, pool: true });
  const fire = barrel(true), ts = tyreStool();
  put(bus(), 104, 90);
  for (const u of [72, 104, 136]) put(ts, u, 114);
  put(fire, 60, 140); put(fire, 156, 78); put(fire, 100, 154);
  put(carSofa(), 144, 138); put(drumTable(), 62, 100);
  pool(60, 130, 56, 24, K.amber, 0.3); pool(156, 68, 56, 24, K.amber, 0.3); pool(100, 144, 56, 24, K.amber, 0.3);
  pool(104, 100, 70, 30, K.red, 0.14); pool(62, 94, 30, 16, K.amber, 0.2);
}
